/**
 * Turning `y = x^2 - 3x + 1` into something that can be evaluated ten thousand times.
 *
 * ## Why this is written even though MathLive is a dependency
 *
 * MathLive *is* used, for input — the panel's formula editor is its `<math-field>`. What it
 * hands back is a LaTeX string, and a string is exactly as far as it goes: nothing in it can
 * be evaluated. KaTeX is the same, one step further removed — it renders LaTeX to HTML and
 * evaluates nothing at all.
 *
 * So a maths library removes the *typing*, not the work below. What a grapher needs is narrow
 * and unglamorous: a small grammar, an evaluator with no allocation in its hot loop, and an
 * honest `null` for every input where the function has no value. None of that ships in a
 * package, and the parser has to exist either way.
 *
 * ## The grammar
 *
 * ```
 *   expression := term (('+' | '-') term)*
 *   term       := power (('*' | '/') power | implicit)*
 *   power      := unary ('^' power)?            -- right-associative: 2^3^2 = 2^9
 *   unary      := '-' unary | primary
 *   primary    := number | constant | variable | call | '(' expression ')'
 *   call       := name '(' expression (',' expression)* ')'
 * ```
 *
 * ## Implicit multiplication, and where it stops
 *
 * `2x`, `3sin(x)` and `2(x+1)` all mean multiplication, because that is what they mean on
 * paper and a teacher typing a textbook expression should not have to think about it.
 *
 * It stops at one place, deliberately: a name immediately followed by `(` is a **call**, not a
 * product. So `sin(x)` is the sine of x, never `s·i·n·(x)`. That is why names are matched
 * against a known table rather than being split into letters — `sin` is one identifier, and an
 * unknown one is an error rather than a silent product of three variables.
 *
 * ## Why `^` and not `**`
 *
 * `^` is what is written on a blackboard, and this is a tool for blackboards.
 *
 * ## LaTeX, read directly
 *
 * The formula editor hands back LaTeX — \frac, \sqrt, \left( — and the obvious move is to
 * translate that into the plain syntax above and parse the result.
 *
 * It is the wrong move. A translation step is a second place for the notation to be defined,
 * it is lossy in both directions, and the round trip is exactly what corrupts a formula the
 * user has already typed once. Instead the *tokeniser* reads both: \frac is a token, braces
 * are grouping brackets, \left( is an ordinary parenthesis. One grammar, two surface
 * syntaxes, and a formula that survives being edited, saved, reopened and edited again.
 *
 * So `x^2` and `x^{2}` are the same expression, and so are `sqrt(x)` and \sqrt{x}. A teacher
 * typing on a keyboard and one clicking in the editor produce the same document.
 */

export type Node =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'variable' }
  | { readonly kind: 'unary'; readonly operand: Node }
  | {
      readonly kind: 'binary';
      readonly operator: '+' | '-' | '*' | '/' | '^';
      readonly left: Node;
      readonly right: Node;
    }
  | { readonly kind: 'call'; readonly name: FunctionName; readonly args: readonly Node[] };

/**
 * The functions a school-level expression may use.
 *
 * Deliberately a closed list. An open one — dispatching to whatever `Math` happens to expose —
 * would accept `Math.random` and produce a graph that is different every frame, and would leak
 * the host language's naming into a notation that is supposed to look like mathematics.
 */
export const FUNCTIONS = {
  sin: { arity: 1, apply: ([x]) => Math.sin(x ?? 0) },
  cos: { arity: 1, apply: ([x]) => Math.cos(x ?? 0) },
  tan: { arity: 1, apply: ([x]) => Math.tan(x ?? 0) },
  asin: { arity: 1, apply: ([x]) => Math.asin(x ?? 0) },
  acos: { arity: 1, apply: ([x]) => Math.acos(x ?? 0) },
  atan: { arity: 1, apply: ([x]) => Math.atan(x ?? 0) },
  sinh: { arity: 1, apply: ([x]) => Math.sinh(x ?? 0) },
  cosh: { arity: 1, apply: ([x]) => Math.cosh(x ?? 0) },
  tanh: { arity: 1, apply: ([x]) => Math.tanh(x ?? 0) },
  sqrt: { arity: 1, apply: ([x]) => Math.sqrt(x ?? 0) },
  abs: { arity: 1, apply: ([x]) => Math.abs(x ?? 0) },
  exp: { arity: 1, apply: ([x]) => Math.exp(x ?? 0) },
  ln: { arity: 1, apply: ([x]) => Math.log(x ?? 0) },
  log: { arity: 1, apply: ([x]) => Math.log10(x ?? 0) },
  floor: { arity: 1, apply: ([x]) => Math.floor(x ?? 0) },
  ceil: { arity: 1, apply: ([x]) => Math.ceil(x ?? 0) },
  round: { arity: 1, apply: ([x]) => Math.round(x ?? 0) },
  sign: { arity: 1, apply: ([x]) => Math.sign(x ?? 0) },
  min: { arity: 2, apply: ([a, b]) => Math.min(a ?? 0, b ?? 0) },
  max: { arity: 2, apply: ([a, b]) => Math.max(a ?? 0, b ?? 0) },
} as const satisfies Record<
  string,
  { readonly arity: number; readonly apply: (args: readonly number[]) => number }
>;

export type FunctionName = keyof typeof FUNCTIONS;

const CONSTANTS: Readonly<Record<string, number>> = {
  pi: Math.PI,
  π: Math.PI,
  e: Math.E,
  tau: Math.PI * 2,
};

/** The variable an expression is a function of. Only one, because a graph has one axis. */
const VARIABLE = 'x';

export class ExpressionError extends Error {
  /** Where in the text the problem is, so the interface can point at it. */
  readonly at: number;

  constructor(message: string, at: number) {
    super(message);
    this.at = at;
  }
}

/* ------------------------------------------------------------------- tokenising */

type Token =
  | { readonly kind: 'number'; readonly value: number; readonly at: number }
  | { readonly kind: 'name'; readonly text: string; readonly at: number }
  | { readonly kind: 'symbol'; readonly text: string; readonly at: number }
  /** A fraction: its two arguments are groups rather than a parenthesised list. */
  | { readonly kind: 'frac'; readonly at: number };

const SYMBOLS = new Set(['+', '-', '*', '/', '^', '(', ')', ',', '{', '}']);

/**
 * LaTeX control words, mapped to what the grammar already understands.
 *
 * Anything absent is rejected rather than ignored. Silently dropping an unknown command would
 * turn an integral into `x` — a perfectly valid expression that is not what was written, and
 * which graphs happily.
 */
const LATEX_WORDS: Readonly<Record<string, string>> = {
  // Grouping and spacing: no meaning to an evaluator, dropped rather than refused.
  left: '',
  right: '',
  ',': '',
  ';': '',
  '!': '',
  quad: '',
  qquad: '',
  displaystyle: '',
  limits: '',

  cdot: '*',
  times: '*',
  div: '/',
  pm: '+',

  // Functions and constants keep their plain-syntax names, so one table serves both.
  sin: 'sin',
  cos: 'cos',
  tan: 'tan',
  arcsin: 'asin',
  arccos: 'acos',
  arctan: 'atan',
  sinh: 'sinh',
  cosh: 'cosh',
  tanh: 'tanh',
  ln: 'ln',
  log: 'log',
  exp: 'exp',
  abs: 'abs',
  min: 'min',
  max: 'max',
  lfloor: 'floor',
  operatorname: '',
  pi: 'pi',
  tau: 'tau',
  mathrm: '',
};

function tokenise(source: string): readonly Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < source.length) {
    const char = source[index] ?? '';

    if (char === ' ' || char === '\t') {
      index += 1;
      continue;
    }

    /*
     * A few characters a teacher will type from habit, mapped to what the grammar knows.
     * Refusing them would be technically correct and practically obstructive: `2×3` is
     * multiplication to everyone who has ever been to school.
     */
    if (char === '×' || char === '·') {
      tokens.push({ kind: 'symbol', text: '*', at: index });
      index += 1;
      continue;
    }
    if (char === '÷' || char === ':') {
      tokens.push({ kind: 'symbol', text: '/', at: index });
      index += 1;
      continue;
    }
    if (char === '−' || char === '–') {
      tokens.push({ kind: 'symbol', text: '-', at: index });
      index += 1;
      continue;
    }

    /*
     * A LaTeX control word.
     *
     * \sqrt becomes the ordinary `sqrt` name. \frac becomes a token of its own,
     * because its two arguments are groups rather than a parenthesised list. \left and
     * \right vanish entirely — they are typesetting, not arithmetic.
     */
    if (char === '\\') {
      const start = index;
      index += 1;

      // A control *symbol* is a single character: the LaTeX spacing commands are all of
      // that shape, and each one maps to nothing at all.
      let word = '';
      if (isLetter(source[index] ?? '')) {
        while (isLetter(source[index] ?? '')) {
          word += source[index] ?? '';
          index += 1;
        }
      } else {
        word = source[index] ?? '';
        index += 1;
      }

      if (word === 'frac' || word === 'dfrac' || word === 'tfrac') {
        tokens.push({ kind: 'frac', at: start });
        continue;
      }
      if (word === 'sqrt') {
        tokens.push({ kind: 'name', text: 'sqrt', at: start });
        continue;
      }

      const mapped = LATEX_WORDS[word];
      if (mapped === undefined) {
        throw new ExpressionError(`không hỗ trợ lệnh \${word}`, start);
      }
      if (mapped === '') continue;

      tokens.push(
        SYMBOLS.has(mapped)
          ? { kind: 'symbol', text: mapped, at: start }
          : { kind: 'name', text: mapped, at: start }
      );
      continue;
    }

    if (SYMBOLS.has(char)) {
      tokens.push({ kind: 'symbol', text: char, at: index });
      index += 1;
      continue;
    }

    if (isDigit(char) || (char === '.' && isDigit(source[index + 1] ?? ''))) {
      const start = index;
      while (isDigit(source[index] ?? '')) index += 1;
      if (source[index] === '.') {
        index += 1;
        while (isDigit(source[index] ?? '')) index += 1;
      }
      // An exponent, so `1e-3` reads as one number rather than a product involving `e`.
      if ((source[index] === 'e' || source[index] === 'E') && isExponent(source, index + 1)) {
        index += 1;
        if (source[index] === '+' || source[index] === '-') index += 1;
        while (isDigit(source[index] ?? '')) index += 1;
      }
      tokens.push({ kind: 'number', value: Number(source.slice(start, index)), at: start });
      continue;
    }

    if (isLetter(char)) {
      const start = index;
      while (isLetter(source[index] ?? '')) index += 1;
      tokens.push({ kind: 'name', text: source.slice(start, index), at: start });
      continue;
    }

    throw new ExpressionError(`không hiểu ký tự "${char}"`, index);
  }

  return tokens;
}

const isDigit = (char: string): boolean => char >= '0' && char <= '9';
const isLetter = (char: string): boolean => /[A-Za-zπ]/.test(char);
const isExponent = (source: string, at: number): boolean => {
  const next = source[at] ?? '';
  return isDigit(next) || ((next === '+' || next === '-') && isDigit(source[at + 1] ?? ''));
};

/* ---------------------------------------------------------------------- parsing */

/**
 * Parses an expression, or the right-hand side of one.
 *
 * `y = x^2` and `f(x) = x^2` and plain `x^2` are all accepted, because all three are how a
 * teacher writes the same thing. The prefix is stripped rather than parsed: this grammar has
 * no notion of assignment, and pretending otherwise would invite `x = 3` to mean something.
 */
export function parseExpression(source: string): Node {
  const body = stripAssignment(source);
  if (body.text.trim() === '') throw new ExpressionError('chưa có biểu thức', body.offset);

  const tokens = tokenise(body.text);
  const state = { tokens, index: 0 };

  const node = parseSum(state);
  const leftover = state.tokens[state.index];
  if (leftover !== undefined) {
    throw new ExpressionError('thừa ký tự ở đây', leftover.at + body.offset);
  }
  return node;
}

function stripAssignment(source: string): { readonly text: string; readonly offset: number } {
  const equals = source.indexOf('=');
  if (equals < 0) return { text: source, offset: 0 };

  const left = source.slice(0, equals).trim();

  // Only a bare `y` or an `f(x)`-style name is a heading.
  if (/^[A-Za-z]\w*(\s*\(\s*[A-Za-z]\w*\s*\))?$/.test(left)) {
    return { text: source.slice(equals + 1), offset: equals + 1 };
  }

  /*
   * Anything else with an `=` is an *equation*, and this tool graphs functions.
   *
   * Saying so is worth a branch of its own. Falling through would reach the tokeniser and
   * produce «không hiểu ký tự "="» — technically true and useless, because the user knows
   * perfectly well what an equals sign is. What they need told is that they have written a
   * thing to *solve* where a thing to *draw* was expected.
   */
  throw new ExpressionError(
    'đây là phương trình, không phải hàm số — hãy viết dạng y = ... để vẽ đồ thị',
    equals
  );
}

interface State {
  readonly tokens: readonly Token[];
  index: number;
}

const peek = (state: State): Token | undefined => state.tokens[state.index];

function eat(state: State, text: string): boolean {
  const token = peek(state);
  if (token?.kind === 'symbol' && token.text === text) {
    state.index += 1;
    return true;
  }
  return false;
}

function parseSum(state: State): Node {
  let left = parseProduct(state);
  for (;;) {
    if (eat(state, '+')) left = { kind: 'binary', operator: '+', left, right: parseProduct(state) };
    else if (eat(state, '-'))
      left = { kind: 'binary', operator: '-', left, right: parseProduct(state) };
    else return left;
  }
}

function parseProduct(state: State): Node {
  let left = parseUnary(state);
  for (;;) {
    if (eat(state, '*')) {
      left = { kind: 'binary', operator: '*', left, right: parseUnary(state) };
    } else if (eat(state, '/')) {
      left = { kind: 'binary', operator: '/', left, right: parseUnary(state) };
    } else if (startsImplicitProduct(state)) {
      /*
       * `2x`, `3sin(x)`, `2(x+1)`, `x(x-1)` — juxtaposition is multiplication, as it is on
       * paper. Binding it at *this* precedence rather than tighter is what makes `1/2x` read
       * as `(1/2)·x`, which is how it is meant nine times out of ten and how a calculator
       * reads it.
       */
      left = { kind: 'binary', operator: '*', left, right: parseUnary(state) };
    } else {
      return left;
    }
  }
}

function startsImplicitProduct(state: State): boolean {
  const token = peek(state);
  if (token === undefined) return false;
  if (token.kind === 'number' || token.kind === 'name') return true;

  // A fraction juxtaposed with anything is a product: `2\frac{1}{x}` is two over x.
  if (token.kind === 'frac') return true;

  // Only an opening bracket — every other symbol is an operator, and `2 + 3` must not become
  // `2 · (+3)`. A LaTeX group counts, so `2{x+1}` reads as it looks.
  return token.text === '(' || token.text === '{';
}

function parseUnary(state: State): Node {
  if (eat(state, '-')) return { kind: 'unary', operand: parseUnary(state) };
  // A leading `+` is noise, and rejecting it helps nobody.
  if (eat(state, '+')) return parseUnary(state);
  return parsePower(state);
}

function parsePower(state: State): Node {
  const base = parsePrimary(state);
  if (!eat(state, '^')) return base;

  /*
   * Right-associative, and the exponent parses as a *unary* so that `2^-1` works. Parsing it
   * as a primary would reject the minus, and `x^-2` is ordinary school notation.
   */
  return { kind: 'binary', operator: '^', left: base, right: parseUnary(state) };
}

function parsePrimary(state: State): Node {
  const token = peek(state);
  if (token === undefined) throw new ExpressionError('biểu thức bị cụt', lastPosition(state));

  if (token.kind === 'number') {
    state.index += 1;
    return { kind: 'number', value: token.value };
  }

  if (token.kind === 'symbol' && token.text === '(') {
    state.index += 1;
    const inner = parseSum(state);
    if (!eat(state, ')')) throw new ExpressionError('thiếu dấu ")"', token.at);
    return inner;
  }

  /*
   * A LaTeX group. `{...}` is exactly `(...)` to an evaluator — it groups and nothing more.
   *
   * Treating it as a bracket rather than as something special is what makes `x^{2}` and `x^2`
   * the same expression, which in turn is what lets a formula typed on a keyboard and one
   * built in the editor produce identical documents.
   */
  if (token.kind === 'symbol' && token.text === '{') {
    state.index += 1;
    const inner = parseSum(state);
    if (!eat(state, '}')) throw new ExpressionError('thiếu dấu "}"', token.at);
    return inner;
  }

  // `\frac{a}{b}`: two groups, no comma, no parentheses. Its own token for that reason.
  if (token.kind === 'frac') {
    state.index += 1;
    const numerator = parseGroup(state, token.at);
    const denominator = parseGroup(state, token.at);
    return { kind: 'binary', operator: '/', left: numerator, right: denominator };
  }

  if (token.kind === 'name') {
    state.index += 1;
    return parseName(state, token);
  }

  throw new ExpressionError(`không dùng được "${token.text}" ở đây`, token.at);
}

function parseName(state: State, token: Token & { kind: 'name' }): Node {
  const lower = token.text.toLowerCase();

  if (lower in FUNCTIONS) {
    const name = lower as FunctionName;

    /*
     * Either bracket opens the argument list.
     *
     * A keyboard user writes `sqrt(x)`; the editor emits a square root whose argument is a
     * brace group. Both are the same call, and accepting only the parenthesis would reject
     * every root the formula editor produces — which is most of the reason it exists.
     */
    const braced = eat(state, '{');
    if (!braced && !eat(state, '(')) {
      throw new ExpressionError(`hàm ${lower} cần dấu "("`, token.at);
    }

    const closing = braced ? '}' : ')';
    const args: Node[] = [parseSum(state)];
    while (eat(state, ',')) args.push(parseSum(state));
    if (!eat(state, closing)) {
      throw new ExpressionError(`thiếu dấu "${closing}"`, token.at);
    }

    const expected = FUNCTIONS[name].arity;
    if (args.length !== expected) {
      throw new ExpressionError(
        `hàm ${lower} cần ${String(expected)} tham số, không phải ${String(args.length)}`,
        token.at
      );
    }
    return { kind: 'call', name, args };
  }

  if (lower in CONSTANTS) return { kind: 'number', value: CONSTANTS[lower] ?? 0 };
  if (lower === VARIABLE) return { kind: 'variable' };

  throw new ExpressionError(`không biết "${token.text}" là gì`, token.at);
}

function lastPosition(state: State): number {
  const last = state.tokens[state.tokens.length - 1];
  return last === undefined ? 0 : last.at;
}

/**
 * One argument of a fraction. Braces required.
 *
 * ## Why the unbraced form is refused
 *
 * In LaTeX proper, each argument is a single *token*, so `\frac12` is one half. That rule
 * cannot be honoured here without unpicking work the tokeniser has already done correctly:
 * `12` has been read as the number twelve by the time this sees it, and `\frac12` would come
 * out as twelve over whatever followed. Splitting a number back into digits to satisfy a
 * notation nobody types is the wrong repair.
 *
 * Every formula editor emits the braced form, so the cost is a clear message on input that in
 * practice arrives only by hand — which is far better than silently reading `\frac12` as
 * twelve and drawing a curve to match.
 */
function parseGroup(state: State, at: number): Node {
  const token = peek(state);
  if (token === undefined) throw new ExpressionError('phân số thiếu tử hoặc mẫu', at);

  if (token.kind !== 'symbol' || token.text !== '{') {
    throw new ExpressionError('phân số cần dạng \\frac{tử}{mẫu}', token.at);
  }

  state.index += 1;
  const inner = parseSum(state);
  if (!eat(state, '}')) throw new ExpressionError('thiếu dấu "}"', token.at);
  return inner;
}
